import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../utils/api';
import { startOfToday } from '../utils/dueDate';
import { buildDueQueueRows, summarizeDueQueue } from '../utils/buildDueQueueRows';

async function fetchList(url) {
  try {
    const response = await api.get(url, { skipErrorToast: true });
    return response.data?.data ?? [];
  } catch {
    return [];
  }
}

/**
 * Single source of truth for Dashboard cards, Due queue, To-Do popup, and Jobs overdue/urgent lists.
 * Same query keys everywhere so React Query keeps them in sync.
 */
export function useDueQueueData() {
  const today = useMemo(() => startOfToday(), []);

  const { data: dueSoonBuildings = [], isLoading: loadingDueSoon } = useQuery({
    // 60d feed keeps "Upcoming (31–60)" in Due queue; Inspections "Due soon" uses days=30 separately
    queryKey: ['buildings', 'due-soon', 60],
    queryFn: () => fetchList('/buildings/due-soon?days=60'),
    retry: false,
  });

  const { data: overdueBuildings = [], isLoading: loadingOdb } = useQuery({
    queryKey: ['buildings', 'overdue'],
    queryFn: () => fetchList('/buildings/overdue'),
    retry: false,
  });

  const { data: overdueJobs = [], isLoading: loadingOj } = useQuery({
    queryKey: ['jobs', 'overdue'],
    queryFn: () => fetchList('/jobs?overdue=1&limit=500'),
    retry: false,
  });

  const { data: urgentJobs = [], isLoading: loadingUj } = useQuery({
    queryKey: ['jobs', 'urgent'],
    queryFn: () => fetchList('/jobs?urgent=1&limit=500'),
    retry: false,
  });

  const { data: upcomingDue, isLoading: loadingUp } = useQuery({
    queryKey: ['jobs', 'upcoming-due'],
    queryFn: async () => {
      try {
        const response = await api.get('/jobs/upcoming-due', { skipErrorToast: true });
        return response.data ?? { data: [], byWeek: {} };
      } catch {
        return { data: [], byWeek: {} };
      }
    },
    retry: false,
  });

  const { data: scheduledJobs = [], isLoading: loadingSch } = useQuery({
    queryKey: ['jobs', 'status', 'SCHEDULED'],
    queryFn: () => fetchList('/jobs?status=SCHEDULED&limit=500'),
    retry: false,
  });

  const { data: inProgressJobs = [], isLoading: loadingIp } = useQuery({
    queryKey: ['jobs', 'status', 'IN_PROGRESS'],
    queryFn: () => fetchList('/jobs?status=IN_PROGRESS&limit=500'),
    retry: false,
  });

  const rows = useMemo(
    () =>
      buildDueQueueRows({
        overdueJobs,
        urgentJobs,
        overdueBuildings,
        dueSoonBuildings,
        upcomingDue,
        today,
      }),
    [overdueJobs, urgentJobs, overdueBuildings, dueSoonBuildings, upcomingDue, today]
  );

  const summary = useMemo(() => summarizeDueQueue(rows), [rows]);

  return {
    rows,
    summary,
    overdueJobs,
    urgentJobs,
    scheduledJobs,
    inProgressJobs,
    isLoading: loadingDueSoon || loadingOdb || loadingOj || loadingUj || loadingUp || loadingSch || loadingIp,
  };
}

/**
 * Map due-queue SS rows into Jobs list row shapes (job | building_due).
 */
export function dueQueueRowsToListRows(queueRows, jobsById = new Map()) {
  return (Array.isArray(queueRows) ? queueRows : []).map((r) => {
    if (r.job_id) {
      const job = jobsById.get(r.job_id) || jobsById.get(String(r.job_id));
      if (job) {
        return { ...job, _type: 'job', _queueKey: r.key };
      }
      return {
        _type: 'job',
        id: r.job_id,
        building_id: r.building_id,
        building_name: r.building_name,
        building_address: r.building_address,
        address: r.building_address,
        company_id: r.company_id,
        company_name: r.company_name,
        ss_code: r.ss_code,
        ss_name: r.ss_name,
        due_date: r.due_date,
        assigned_worker_id: r.assigned_worker_id,
        assigned_worker_name: r.assigned_worker_name,
        application_approved: r.application_approved,
        status: null,
        _queueKey: r.key,
      };
    }
    return {
      _type: 'building_due',
      id: r.key,
      building_id: r.building_id,
      building_name: r.building_name,
      address: r.building_address,
      company_id: r.company_id,
      company_name: r.company_name,
      due_date: r.due_date,
      ss_codes: r.ss_code ? [r.ss_code] : [],
      ss_list: [
        {
          ss_code: r.ss_code,
          ss_name: r.ss_name,
          due_date: r.due_date,
          default_assignee_name: r.default_assignee_name,
          default_assignee_type: r.default_assignee_type,
          default_worker_id: r.default_worker_id,
          inspection_frequency: r.inspection_frequency,
        },
      ],
      application_approved: r.application_approved,
      default_assignee_name: r.default_assignee_name,
      default_assignee_type: r.default_assignee_type,
      default_worker_id: r.default_worker_id,
      inspection_frequency: r.inspection_frequency,
      _queueKey: r.key,
    };
  });
}
